import {
  CopilotChatAssistantMessage,
  CopilotChatMessageView,
  useAgent,
} from '@copilotkit/react-core/v2'
import { useRouter } from 'next/router'
import { useCallback, useRef } from 'react'
import { Streamdown } from 'streamdown'
import { useAiChatContext } from './AiChatContext'
import { HumanModeCountdown } from './HumanModeCountdown'
import { TimeoutContactBanner } from './TimeoutContactBanner'

const INTENT_RE = /\{"intent"\s*:\s*"[^"]*"\}/gi
const URL_KEY_RE = /\s*\(url_key:\s*[^)]+\)/gi
const COUNTDOWN_ID = '__human_mode_countdown__'
const CONTACT_FORM_ID = '__contact_form__'

function stripIntent(text: string): string {
  return text.replace(INTENT_RE, '').trim()
}

function stripUrlKey(text: string): string {
  return text.replace(URL_KEY_RE, '').trim()
}

const INTERNAL_HOST_RE = /(?:^|\.)templi\.com$/i

function isInternal(href: string): boolean {
  if (!href || href.startsWith('#') || href.startsWith('/')) return true
  try {
    return INTERNAL_HOST_RE.test(new URL(href).hostname)
  } catch {
    return false
  }
}

function ChatLink({
  href = '',
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { mode, onClose } = useAiChatContext()
  const router = useRouter()
  const internal = isInternal(href)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!internal) return
    e.preventDefault()
    if (mode === 'chat') onClose()
    router.push(href).catch(() => {})
    props.onClick?.(e)
  }

  return internal ? (
    <a href={href} {...props} target='_self' rel={undefined} onClick={handleClick}>
      {children}
    </a>
  ) : (
    <a href={href} {...props} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  )
}

const markdownComponents = { a: ChatLink }

const CustomMarkdownRenderer = Object.assign(
  ({
    content,
    className,
    ...props
  }: React.ComponentProps<typeof CopilotChatAssistantMessage.MarkdownRenderer>) => (
    <Streamdown className={className} components={markdownComponents} {...props}>
      {content ?? ''}
    </Streamdown>
  ),
  CopilotChatAssistantMessage.MarkdownRenderer,
)

// CustomAssistantMessage reads context directly so synthetic messages can access
// the latest humanModeStartedAt / showContactForm without prop drilling.
const CustomAssistantMessage = Object.assign(
  (props: React.ComponentProps<typeof CopilotChatAssistantMessage>) => {
    const { humanModeActive, humanModeStartedAt, showContactForm, setShowContactForm } =
      useAiChatContext()
    const { agent } = useAgent()
    const msgId = (props as any).message?.id

    const handleFormSubmit = useCallback(() => {
      if (agent.state) {
        agent.setState({ ...agent.state, human_mode: false })
      }
    }, [agent, setShowContactForm])

    if (msgId === COUNTDOWN_ID) {
      if (!humanModeActive || !humanModeStartedAt || showContactForm) return null
      return (
        <div className='px-4 pb-2'>
          <HumanModeCountdown startedAt={humanModeStartedAt} />
        </div>
      )
    }

    if (msgId === CONTACT_FORM_ID) {
      if (!showContactForm) return null
      return (
        <div className='px-4 pb-4'>
          <TimeoutContactBanner
            onDismiss={() => setShowContactForm(false)}
            onSubmit={handleFormSubmit}
          />
        </div>
      )
    }

    return <CopilotChatAssistantMessage markdownRenderer={CustomMarkdownRenderer} {...props} />
  },
  CopilotChatAssistantMessage,
)

function FilteredMessageViewBase({
  messages,
  ...rest
}: React.ComponentProps<typeof CopilotChatMessageView>) {
  const { humanModeActive, showContactForm } = useAiChatContext()

  // Capture the filtered message count the first time showContactForm becomes
  // true so the form is inserted at that position on every subsequent render,
  // keeping it inline as new messages arrive below it.
  const contactFormInsertAt = useRef<number | null>(null)
  const prevShowContactForm = useRef(false)

  const filtered: typeof messages = (messages
    ?.filter((m) => {
      if (m.role !== 'assistant') return true
      const { toolCalls } = m as { toolCalls?: unknown[] }
      if (Array.isArray(toolCalls) && toolCalls.length > 0) return true
      const text = typeof m.content === 'string' ? m.content.trim() : ''
      return text.replace(INTENT_RE, '').trim() !== ''
    })
    .map((m) => {
      if (typeof m.content !== 'string') return m
      if (m.role === 'user') {
        const stripped = stripUrlKey(m.content)
        return stripped === m.content ? m : { ...m, content: stripped }
      }
      if (m.role === 'assistant') {
        const stripped = stripIntent(m.content)
        return stripped === m.content ? m : { ...m, content: stripped }
      }
      return m
    }) ?? []) as typeof messages

  if (!showContactForm) {
    contactFormInsertAt.current = null
    prevShowContactForm.current = false
  } else if (!prevShowContactForm.current) {
    contactFormInsertAt.current = (filtered ?? []).length
    prevShowContactForm.current = true
  }

  // Inject synthetic messages so countdown / contact form appear inline
  // with the chat history rather than pinned outside the scroll area.
  const withSynthetic: typeof messages = [...(filtered ?? [])] as typeof messages
  if (humanModeActive && !showContactForm) {
    ;(withSynthetic as any[]).push({ id: COUNTDOWN_ID, role: 'assistant', content: '' })
  }
  if (showContactForm) {
    const insertAt = contactFormInsertAt.current ?? (withSynthetic as any[]).length
    ;(withSynthetic as any[]).splice(insertAt, 0, { id: CONTACT_FORM_ID, role: 'assistant', content: '' })
  }

  return (
    <CopilotChatMessageView
      messages={withSynthetic}
      assistantMessage={CustomAssistantMessage}
      {...rest}
    />
  )
}

// Copy static slots (Cursor, AssistantMessage, etc.) so the prop type is satisfied.
export const FilteredMessageView = Object.assign(FilteredMessageViewBase, CopilotChatMessageView)
